import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import AppShell from "@/components/AppShell";
import HomePage from "@/pages/HomePage";
// E2 — route-level code splitting; the current route's chunk starts downloading immediately (parallel with /auth/me),
// the rest are prefetched when the browser is idle.
const LOADERS = {
  SettingsPage: () => import("@/pages/SettingsPage"),
  SettingsPropertiesPage: () => import("@/pages/SettingsPropertiesPage"),
  LineSettingsRedirect: () => import("@/pages/LineSettingsRedirect"),
  MetaPocPage: () => import("@/pages/MetaPocPage"),
  LinePocPage: () => import("@/pages/LinePocPage"),
  LineInsightsPage: () => import("@/pages/LineInsightsPage"),
  ComparePage: () => import("@/pages/ComparePage"),
  FunnelPage: () => import("@/pages/FunnelPage"),
  SettingsRevenuePage: () => import("@/pages/SettingsRevenuePage"),
  SettingsLineSourcesPage: () => import("@/pages/SettingsLineSourcesPage"),
  SettingsDataHealthPage: () => import("@/pages/SettingsDataHealthPage"),
  SettingsPmsPage: () => import("@/pages/SettingsPmsPage"),
  SettingsRulesPage: () => import("@/pages/SettingsRulesPage"),
  ClassificationReviewPage: () => import("@/pages/ClassificationReviewPage"),
  AdvisorPage: () => import("@/pages/AdvisorPage"),
};
const ROUTE_LOADER = {
  "/settings": LOADERS.SettingsPage,
  "/settings/properties": LOADERS.SettingsPropertiesPage,
  "/meta-poc": LOADERS.MetaPocPage,
  "/line-poc": LOADERS.LinePocPage,
  "/line-classification-review": LOADERS.ClassificationReviewPage,
  "/line-insights": LOADERS.LineInsightsPage,
  "/compare": LOADERS.ComparePage,
  "/funnel": LOADERS.FunnelPage,
  "/settings/revenue": LOADERS.SettingsRevenuePage,
  "/settings/line-sources": LOADERS.SettingsLineSourcesPage,
  "/settings/data-health": LOADERS.SettingsDataHealthPage,
  "/settings/integrations/pms": LOADERS.SettingsPmsPage,
  "/settings/rules": LOADERS.SettingsRulesPage,
  "/advisor": LOADERS.AdvisorPage,
};
ROUTE_LOADER[window.location.pathname]?.();
const _idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1500));
setTimeout(() => _idle(() => Object.values(LOADERS).forEach((load) => load().catch(() => {}))), 4000);
const SettingsPage = lazy(LOADERS.SettingsPage);
const SettingsPropertiesPage = lazy(LOADERS.SettingsPropertiesPage);
const LineSettingsRedirect = lazy(LOADERS.LineSettingsRedirect);
const MetaPocPage = lazy(LOADERS.MetaPocPage);
const LinePocPage = lazy(LOADERS.LinePocPage);
const LineInsightsPage = lazy(LOADERS.LineInsightsPage);
const ComparePage = lazy(LOADERS.ComparePage);
const FunnelPage = lazy(LOADERS.FunnelPage);
const SettingsRevenuePage = lazy(LOADERS.SettingsRevenuePage);
const SettingsLineSourcesPage = lazy(LOADERS.SettingsLineSourcesPage);
const SettingsDataHealthPage = lazy(LOADERS.SettingsDataHealthPage);
const SettingsPmsPage = lazy(LOADERS.SettingsPmsPage);
const SettingsRulesPage = lazy(LOADERS.SettingsRulesPage);
const ClassificationReviewPage = lazy(LOADERS.ClassificationReviewPage);
const AdvisorPage = lazy(LOADERS.AdvisorPage);
import LoginPage from "@/pages/LoginPage";
import { AuthProvider, useAuth } from "@/components/AuthContext";
import { AnimatePresence, motion } from "framer-motion";
import { Loader2 } from "lucide-react";

function PageTransition({ children }) {
  const loc = useLocation();
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={loc.pathname}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -6 }}
        transition={{ duration: 0.2 }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
}

function RequireAuth({ children }) {
  const { authenticated } = useAuth();
  if (authenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="auth-checking">
        <Loader2 className="h-5 w-5 animate-spin text-[#F26A1F]" />
      </div>
    );
  }
  if (!authenticated) return <Navigate to="/login" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/*"
        element={
          <RequireAuth>
            <AppShell>
              <PageTransition>
                <Suspense fallback={<div className="flex items-center justify-center py-24 text-slate-400" data-testid="route-loading"><Loader2 className="h-5 w-5 animate-spin" /></div>}>
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route path="/settings/line" element={<LineSettingsRedirect />} />
                  <Route path="/settings/properties" element={<SettingsPropertiesPage />} />
                  <Route path="/settings/data-health" element={<SettingsDataHealthPage />} />
                  <Route path="/meta-poc" element={<MetaPocPage />} />
                  <Route path="/line-insights" element={<LineInsightsPage />} />
                  <Route path="/line-poc" element={<LinePocPage />} />
                  <Route path="/line-classification-review" element={<ClassificationReviewPage />} />
                  <Route path="/compare" element={<ComparePage />} />
                  <Route path="/funnel" element={<FunnelPage />} />
                  <Route path="/settings/revenue" element={<SettingsRevenuePage />} />
                  <Route path="/settings/line-sources" element={<SettingsLineSourcesPage />} />
                  <Route path="/settings/integrations/pms" element={<SettingsPmsPage />} />
                  <Route path="/settings/rules" element={<SettingsRulesPage />} />
                  <Route path="/advisor" element={<AdvisorPage />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                </Suspense>
              </PageTransition>
            </AppShell>
          </RequireAuth>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <TooltipProvider delayDuration={100} skipDelayDuration={0}>
            <AppRoutes />
            <Toaster richColors position="top-right" />
          </TooltipProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
